import { useMutation, useQueryClient } from "@tanstack/react-query";
import { addItem, findBy, recordEvent, removeItem, setItem, updateAssetState } from "./assetScanStore";
import { scanListKey, useGeotabCall } from "./useScanList";
import { useGeotabClient } from "@/components/GeotabClientContext";
import AddInIds from "@/constants/AddInIds";
import { ScanAsset, ScanEvent, ScanEventType, Stored } from "@/models/assetScan/AssetScanTypes";

const ADD_IN_IDS = { assets: AddInIds.assetScanAssets, events: AddInIds.assetScanEvents };

type SaveInput<T> =
  | { kind: "add"; details: T; groupIds?: string[] }
  | { kind: "set"; row: Stored<T>; details: T; groupIds?: string[] }
  | { kind: "remove"; row: Stored<T> };

/** Add, change or remove one row of a list; the list is read again afterwards. */
export function useScanSave<T>(addInId: string) {
  const { activeSessionId } = useGeotabClient();
  const call = useGeotabCall();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: SaveInput<T>) => {
      if (input.kind === "add") return addItem(call, addInId, input.details, input.groupIds);
      if (input.kind === "set") {
        await setItem(call, addInId, input.row.id, input.details, input.row.details, input.groupIds);
        return input.row.id;
      }
      await removeItem(call, addInId, input.row.id);
      return input.row.id;
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: scanListKey(addInId, activeSessionId) }),
  });
}

const invalidateScanData = (queryClient: ReturnType<typeof useQueryClient>, activeSessionId: string | null) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: scanListKey(AddInIds.assetScanAssets, activeSessionId) }),
    queryClient.invalidateQueries({ queryKey: scanListKey(AddInIds.assetScanEvents, activeSessionId) }),
  ]);

/** Saves a scan: the event, then the asset's state from its whole history (read fresh, not cached). */
export function useRecordEvent() {
  const { activeSessionId } = useGeotabClient();
  const call = useGeotabCall();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ event, asset, types }: { event: ScanEvent; asset: Stored<ScanAsset>; types: ScanEventType[] }) => {
      const history = await findBy<ScanEvent>(call, AddInIds.assetScanEvents, "asset", asset.details.tag);
      return recordEvent(call, { addInIds: ADD_IN_IDS, event, asset, history: history.map(row => row.details), types });
    },
    onSettled: () => invalidateScanData(queryClient, activeSessionId),
  });
}

/** Changes or removes events of one asset, then rebuilds its state from what is left. */
export function useEditEvents() {
  const { activeSessionId } = useGeotabClient();
  const call = useGeotabCall();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      asset,
      types,
      change,
    }: {
      asset: Stored<ScanAsset>;
      types: ScanEventType[];
      change?: { row: Stored<ScanEvent>; details?: ScanEvent };
    }) => {
      if (change?.details) {
        await setItem(call, AddInIds.assetScanEvents, change.row.id, change.details, change.row.details, asset.details.groupIds);
      } else if (change) {
        await removeItem(call, AddInIds.assetScanEvents, change.row.id);
      }
      const history = await findBy<ScanEvent>(call, AddInIds.assetScanEvents, "asset", asset.details.tag);
      await updateAssetState(call, AddInIds.assetScanAssets, asset, history.map(row => row.details), types);
    },
    onSettled: () => invalidateScanData(queryClient, activeSessionId),
  });
}
